import { useState, useMemo, useContext, createContext, useRef } from 'react'
import Dialog from '@mui/material/Dialog'
import useCallbackDebounced from '../../../hooks/shared/useCallbackDebounced'
import TemplatePanel from '../../features/templates/TemplatePanel'

const EditTemplateDialogContext = createContext<{
  openEditTemplateDialog: (args: {
    defaultTemplateJSON?: any | null
    onSave?: () => void
    onClose?: () => void
  }) => void

  closeEditTemplateDialog: () => void
} | null>(null)

const useEditTemplateDialog = () => {
  const context = useContext(EditTemplateDialogContext)
  if (!context) {
    throw new Error('no EditTemplateDialogProvider found when calling useEditTemplateDialog()')
  }
  return context
}

const EditTemplateDialogProvider = ({ children }) => {
  const [open, setOpen] = useState(false)
  const onSaveRef = useRef<() => void>()
  const onCloseRef = useRef<() => void>()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [defaultTemplateJSON, setDefaultTemplateJSON] = useState<any | null>()
  const openEditTemplateDialog = useCallbackDebounced(
    ({
      onSave,
      onClose,
      defaultTemplateJSON,
    }: {
      onSave?: () => void
      onClose?: () => void
      defaultTemplateJSON?: any | null
    }) => {
      setDefaultTemplateJSON(defaultTemplateJSON)
      onSaveRef.current = onSave
      onCloseRef.current = onClose
      setOpen(true)
    },
    [],
  )
  const closeEditTemplateDialog = useCallbackDebounced(() => {
    onCloseRef.current && onCloseRef.current()
    setOpen(false)
  }, [])
  const onSaveEditTemplateDialog = useCallbackDebounced(() => {
    onSaveRef.current && onSaveRef.current()
    setOpen(false)
  }, [])
  const onExited = useCallbackDebounced(() => {
    onSaveRef.current = undefined
    onCloseRef.current = undefined
    setDefaultTemplateJSON(undefined)
  }, [])
  const value = useMemo(
    () => ({
      openEditTemplateDialog,
      closeEditTemplateDialog,
    }),
    [closeEditTemplateDialog, openEditTemplateDialog],
  )
  return (
    <EditTemplateDialogContext.Provider value={value}>
      {children}
      <Dialog
        fullScreen
        open={open}
        onClose={closeEditTemplateDialog}
        slotProps={{
          transition: {
            onExited,
          },
        }}
        sx={{
          zIndex: 20,
        }}
        disableEscapeKeyDown
      >
        <TemplatePanel
          onClose={closeEditTemplateDialog}
          onSave={onSaveEditTemplateDialog}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          defaultTemplateJSON={defaultTemplateJSON}
        />
      </Dialog>
    </EditTemplateDialogContext.Provider>
  )
}

export { useEditTemplateDialog, EditTemplateDialogProvider }
export default EditTemplateDialogProvider
